import { useEffect } from "react";
import { gql, useQuery } from "@apollo/client";
import { driver } from "driver.js";
import "driver.js/dist/driver.css";
import { useAuth } from "./auth";

type Announcement = {
  id: string;
  selector: string;
  title: string;
  description: string;
  expiresAt: string; // ISO date string e.g. "2026-04-11"
  side?: "top" | "bottom" | "left" | "right";
  buttonText?: string;
  /** Only show if workspace createdAt is strictly before this ISO date (existing users at ship time). */
  createdBefore?: string;
};

const announcements: Announcement[] = [
  {
    id: "integrations-sendgrid-ses",
    selector: '[data-announcement="integrations"]',
    title: 'Integrations <span class="announcement-badge">New</span>',
    description:
      "SendGrid and Amazon SES are available. Open Integrations to connect a provider and sync your saved templates.",
    expiresAt: "2026-12-31",
    // Shipped 2026-10-20. Skip workspaces created on or after the next day.
    createdBefore: "2026-10-21T00:00:00.000Z",
    side: "right",
    buttonText: "Got it",
  },
  {
    id: "dark-mode",
    selector: "#account-menu",
    title: 'Dark mode <span class="announcement-badge">New</span>',
    description:
      "Prefer a darker workspace? Open your account menu to switch between Light, Dark, or System.",
    expiresAt: "2026-09-30",
    // App theme shipped 2026-07-29 — skip users who signed up on/after that day.
    createdBefore: "2026-07-29T00:00:00.000Z",
    side: "bottom",
    buttonText: "Got it",
  },
];

const ACCOUNT_CREATED_QUERY = gql`
  query AnnouncementAccountCreatedAt {
    getAccount {
      id
      createdAt
    }
  }
`;

const STORAGE_PREFIX = "announcement-dismissed:";

function isDismissed(id: string): boolean {
  try {
    return localStorage.getItem(STORAGE_PREFIX + id) === "true";
  } catch {
    return false;
  }
}

function dismiss(id: string): void {
  try {
    localStorage.setItem(STORAGE_PREFIX + id, "true");
  } catch {}
}

function isExpired(expiresAt: string): boolean {
  return new Date() > new Date(expiresAt);
}

function isAccountCreatedBefore(
  accountCreatedAt: string | undefined | null,
  createdBefore: string
): boolean {
  if (!accountCreatedAt) return false;
  const created = Date.parse(accountCreatedAt);
  const cutoff = Date.parse(createdBefore);
  if (Number.isNaN(created) || Number.isNaN(cutoff)) return false;
  return created < cutoff;
}

function getActiveAnnouncement(
  accountCreatedAt: string | undefined | null
): Announcement | null {
  for (const a of announcements) {
    if (isExpired(a.expiresAt)) continue;
    if (isDismissed(a.id)) continue;
    if (
      a.createdBefore &&
      !isAccountCreatedBefore(accountCreatedAt, a.createdBefore)
    ) {
      continue;
    }
    return a;
  }
  return null;
}

function findVisibleTarget(selector: string): Element | null {
  const nodes = document.querySelectorAll(selector);
  for (const node of nodes) {
    if (node.getClientRects().length > 0) return node;
  }
  return null;
}

function showAnnouncement(announcement: Announcement, element: Element): void {
  const driverObj = driver({
    showProgress: false,
    showButtons: ["next"],
    popoverClass: "postdrop-announcement",
    nextBtnText: announcement.buttonText || "OK",
    onDestroyStarted: () => {
      dismiss(announcement.id);
      driverObj.destroy();
    },
  });

  driverObj.highlight({
    element,
    popover: {
      title: announcement.title,
      description: announcement.description,
      side: announcement.side || "bottom",
      align: "start",
      // highlight() clears buttons and does not advance unless the step handles it.
      showButtons: ["next"],
      onNextClick: () => {
        dismiss(announcement.id);
        driverObj.destroy();
      },
    },
  });
}

export function FeatureAnnouncements() {
  const { user, loading } = useAuth();
  const { data, loading: accountLoading } = useQuery(ACCOUNT_CREATED_QUERY, {
    skip: loading || !user,
  });

  useEffect(() => {
    if (loading || !user || accountLoading) return;

    const announcement = getActiveAnnouncement(data?.getAccount?.createdAt);
    if (!announcement) return;

    // Wait for the target element to appear in the DOM (editor may render async)
    let attempts = 0;
    const maxAttempts = 20; // ~10 seconds
    const interval = setInterval(() => {
      attempts++;
      const el = findVisibleTarget(announcement.selector);
      if (el) {
        clearInterval(interval);
        // Small extra delay so layout settles
        setTimeout(() => showAnnouncement(announcement, el), 300);
      } else if (attempts >= maxAttempts) {
        clearInterval(interval);
      }
    }, 500);

    return () => clearInterval(interval);
  }, [user, loading, accountLoading, data?.getAccount?.createdAt]);

  return null;
}
